import { formatBuildVersionLabel, formatSemverBuildId } from '@applicator/shared'

export type AppEnvironment = 'development' | 'staging' | 'production'

export type AnalyticsEnvironment = 'staging' | 'production'

/** Runtime app environment for monitoring (Sentry) and analytics tags. */
export function getAppEnvironment(): AppEnvironment {
  const appEnv = import.meta.env.VITE_APP_ENV
  if (appEnv === 'staging' || appEnv === 'production') {
    return appEnv
  }
  if (import.meta.env.DEV) {
    return 'development'
  }
  return import.meta.env.MODE === 'production' ? 'production' : 'development'
}

/** PostHog person/event property — maps local dev to staging for funnel consistency. */
export function getAnalyticsEnvironment(): AnalyticsEnvironment {
  const env = getAppEnvironment()
  return env === 'production' ? 'production' : 'staging'
}

export function getAppRelease(): string | undefined {
  const release = import.meta.env.VITE_APP_RELEASE?.trim()
  return release || undefined
}

/** Pages deployment id or git SHA — ties Sentry events to a specific deploy. */
export function getAppDist(): string | undefined {
  const dist = import.meta.env.VITE_APP_DIST?.trim() || getAppRelease()
  return dist || undefined
}

/** Semver product version from package.json (VITE_APP_VERSION at build). */
export function getAppVersion(): string | undefined {
  const version = import.meta.env.VITE_APP_VERSION?.trim()
  return version || undefined
}

/** Semver build id for support: 1.0.0+abc1234 */
export function getSemverBuildId(): string | undefined {
  const version = getAppVersion()
  if (!version) {
    return getAppRelease() || undefined
  }
  return formatSemverBuildId(version, getAppRelease())
}

/** Profile / footer label: v1.0.0+abc1234 */
export function getBuildVersionLabel(): string | undefined {
  const version = getAppVersion()
  if (!version) {
    const release = getAppRelease()
    return release ? `build ${release}` : undefined
  }
  const label = formatBuildVersionLabel(version, getAppRelease())
  return label || undefined
}
